import {
  Box,
  Divider,
  Paper,
  Popover,
  Typography,
  useTheme,
} from "@mui/material";
import { Plus as PlusIcon } from "lucide-react";
import * as React from "react";

import { CreateCollection } from "@/shared/components/collections/CreateCollection";
import { Button } from "@/shared/foundation/primitives/Button";
import { usePopupState } from "@/shared/hooks";

import styles from "./CreateCollectionButton.module.css";

interface CreateCollectionButtonProps {
  label?: string;
  variant?: "contained" | "outlined" | "filter";
  menuOrigin?: "left" | "center" | "right";
}

export function CreateCollectionButton({
  label = "New",
  variant = "contained",
  menuOrigin = "right",
}: CreateCollectionButtonProps) {
  const popup = usePopupState();
  const theme = useTheme();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (popup.isOpen) {
      popup.close();
    } else {
      popup.open({ anchorEl: event.currentTarget });
    }
  };

  const muiVariant = variant === "filter" ? "outlined" : variant;
  const triggerClassMap: Record<typeof variant, string | undefined> = {
    contained: styles.trigger,
    filter: styles.filterTrigger,
    outlined: undefined,
  };
  const triggerClass = triggerClassMap[variant];

  return (
    <>
      <Button
        variant={muiVariant}
        color={
          muiVariant === "outlined" && variant !== "filter"
            ? "primary"
            : undefined
        }
        size="small"
        startIcon={
          <PlusIcon className={styles.triggerIcon} aria-hidden="true" />
        }
        className={triggerClass}
        onClick={handleClick}
        aria-haspopup="dialog"
        aria-expanded={popup.isOpen}
      >
        {label}
      </Button>
      <Popover
        open={popup.isOpen}
        anchorEl={popup.anchor}
        onClose={popup.close}
        anchorOrigin={{ vertical: "bottom", horizontal: menuOrigin }}
        transformOrigin={{ vertical: "top", horizontal: menuOrigin }}
        marginThreshold={5}
        slotProps={{
          paper: {
            role: "dialog",
            "aria-label": "Create collection",
          },
        }}
      >
        <Box
          component={Paper}
          elevation={24}
          sx={{ width: "24rem", padding: theme.spacing(1.5) }}
        >
          <Typography variant="subtitle1">New Collection</Typography>
          <Divider sx={{ mt: theme.spacing(1), mb: theme.spacing(1) }} />
          <CreateCollection close={popup.close} />
        </Box>
      </Popover>
    </>
  );
}
